/**
 * Centralized theme, icon, and layout constants for proposals.
 * Used by ProposalView, ProposalPreview, and ProposalNew.
 */
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
	Box,
	Building2,
	Clock,
	CreditCard,
	FileText,
	Globe,
	Image,
	LayoutGrid,
	Lock,
	Mail,
	Megaphone,
	MessageCircle,
	MessageSquare,
	Phone,
	Puzzle,
	RefreshCw,
	Server,
	Shield,
	ShoppingCart,
	Star,
	Type,
	Zap,
} from "lucide-react";

// ── Icon map for sections ───────────────────────────────────────
export const ICON_MAP = {
	server: Server,
	shield: Shield,
	globe: Globe,
	zap: Zap,
	lock: Lock,
	mail: Mail,
	phone: Phone,
	message: MessageSquare,
	refresh: RefreshCw,
	box: Box,
	star: Star,
	clock: Clock,
};

// ── Per-item color themes ───────────────────────────────────────
export const ITEM_THEMES = [
	{
		badge: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
		icon: "text-cyan-400",
		iconBg: "bg-cyan-500/10",
		cardHover: "hover:border-cyan-500/30",
		pricingBg: "bg-cyan-500/5 border-cyan-500/10",
		priceText: "text-cyan-400",
		glow: "bg-cyan-500/10",
		featureBg: "bg-cyan-500/5 border-cyan-500/10",
		featureIcon: "text-cyan-400",
	},
	{
		badge: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
		icon: "text-emerald-400",
		iconBg: "bg-emerald-500/10",
		cardHover: "hover:border-emerald-500/30",
		pricingBg: "bg-emerald-500/5 border-emerald-500/10",
		priceText: "text-emerald-400",
		glow: "bg-emerald-500/10",
		featureBg: "bg-emerald-500/5 border-emerald-500/10",
		featureIcon: "text-emerald-400",
	},
	{
		badge: "bg-purple-500/10 border-purple-500/20 text-purple-400",
		icon: "text-purple-400",
		iconBg: "bg-purple-500/10",
		cardHover: "hover:border-purple-500/30",
		pricingBg: "bg-purple-500/5 border-purple-500/10",
		priceText: "text-purple-400",
		glow: "bg-purple-500/10",
		featureBg: "bg-purple-500/5 border-purple-500/10",
		featureIcon: "text-purple-400",
	},
	{
		badge: "bg-blue-500/10 border-blue-500/20 text-blue-400",
		icon: "text-blue-400",
		iconBg: "bg-blue-500/10",
		cardHover: "hover:border-blue-500/30",
		pricingBg: "bg-blue-500/5 border-blue-500/10",
		priceText: "text-blue-400",
		glow: "bg-blue-500/10",
		featureBg: "bg-blue-500/5 border-blue-500/10",
		featureIcon: "text-blue-400",
	},
	{
		badge: "bg-amber-500/10 border-amber-500/20 text-amber-400",
		icon: "text-amber-400",
		iconBg: "bg-amber-500/10",
		cardHover: "hover:border-amber-500/30",
		pricingBg: "bg-amber-500/5 border-amber-500/10",
		priceText: "text-amber-400",
		glow: "bg-amber-500/10",
		featureBg: "bg-amber-500/5 border-amber-500/10",
		featureIcon: "text-amber-400",
	},
];

// ── Theme name → index mapping ──────────────────────────────────
export const THEME_INDEX_MAP = {
	cyan: 0,
	emerald: 1,
	purple: 2,
	blue: 3,
	amber: 4,
};

// ── Section layout options (for UI dropdowns) ───────────────────
export const SECTION_LAYOUT_OPTIONS = [
	{ value: "full-width", label: "Largura Total" },
	{ value: "two-column", label: "Duas Colunas" },
	{ value: "centered", label: "Centralizado" },
	{ value: "card-highlight", label: "Card Destaque" },
];

// ── Item layout options (for UI dropdowns) ──────────────────────
export const ITEM_LAYOUT_OPTIONS = [
	{ value: "auto", label: "Automatico" },
	{ value: "full-width", label: "Largura Total" },
	{ value: "compact-card", label: "Card Compacto" },
	{ value: "featured", label: "Destaque" },
];

// ── Item theme options (for UI dropdowns) ───────────────────────
export const ITEM_THEME_OPTIONS = [
	{ value: "auto", label: "Automatico" },
	{ value: "cyan", label: "Cyan" },
	{ value: "emerald", label: "Emerald" },
	{ value: "purple", label: "Purple" },
	{ value: "blue", label: "Blue" },
	{ value: "amber", label: "Amber" },
];

// ── Icon options (for UI dropdowns) ─────────────────────────────
export const ICON_OPTIONS = [
	{ value: "", label: "Sem icone" },
	{ value: "server", label: "Server" },
	{ value: "shield", label: "Shield" },
	{ value: "globe", label: "Globe" },
	{ value: "zap", label: "Zap" },
	{ value: "lock", label: "Lock" },
	{ value: "star", label: "Star" },
	{ value: "clock", label: "Clock" },
	{ value: "mail", label: "Mail" },
	{ value: "box", label: "Box" },
	{ value: "refresh", label: "Refresh" },
	{ value: "phone", label: "Phone" },
	{ value: "message", label: "Message" },
];

// ── Section types ───────────────────────────────────────────────
export const SECTION_TYPES = [
	{ value: "company_intro", label: "Quem Somos", icon: Building2, max: 1 },
	{ value: "custom", label: "Texto Livre", icon: Type },
	{ value: "features_grid", label: "Grid de Cards", icon: LayoutGrid },
	{ value: "image_gallery", label: "Galeria de Imagens", icon: Image },
	{ value: "cta", label: "Destaque / CTA", icon: Megaphone },
];

// ── Text alignment options ──────────────────────────────────────
export const TEXT_ALIGN_OPTIONS = [
	{ value: "left", label: "Esquerda", icon: AlignLeft },
	{ value: "center", label: "Centro", icon: AlignCenter },
	{ value: "right", label: "Direita", icon: AlignRight },
];

// ── Section background style options ────────────────────────────
export const BG_STYLE_OPTIONS = [
	{ value: "default", label: "Padrao" },
	{ value: "dark", label: "Escuro" },
	{ value: "accent", label: "Destaque Cyan" },
	{ value: "gradient", label: "Gradiente" },
];

// ── Section padding options ─────────────────────────────────────
export const PADDING_OPTIONS = [
	{ value: "sm", label: "Compacto" },
	{ value: "md", label: "Normal" },
	{ value: "lg", label: "Amplo" },
];

// ── Template categories ─────────────────────────────────────────
export const TEMPLATE_CATEGORIES = [
	{
		value: "full",
		label: "Template Completo",
		icon: Puzzle,
		color: "cyan",
		description: "Preenche todas as secoes de uma vez",
	},
	{
		value: "hero",
		label: "Hero Cards",
		icon: CreditCard,
		color: "purple",
		description: "Cards do cabecalho da proposta",
	},
	{
		value: "products",
		label: "Planos / Produtos",
		icon: ShoppingCart,
		color: "emerald",
		description: "Itens pre-selecionados (planos e modulos)",
	},
	{
		value: "content",
		label: "Conteudo",
		icon: FileText,
		color: "blue",
		description: "Secoes de conteudo (Quem Somos, informativo, etc.)",
	},
	{
		value: "footer",
		label: "Rodape / CTA",
		icon: MessageCircle,
		color: "amber",
		description: "Secao de encerramento e call-to-action",
	},
];
